import { convertStringToDouble } from '../js/utils-convert-numbers.js';
import {
  convertStringAmountToCurrencyAndSignDirectionForLanguage,
  getCurrencyCultureSymbolOnLeftOrRight,
} from '../js/utils-convert-currency.js';

// Utility to find the totals record for a given SKU & Store
function findTotalsRecord(skuId, storeId, totalsList) {
  if (!Array.isArray(totalsList) || !totalsList.length) return null;
  return totalsList.find(tt => tt.SKUID === skuId && tt.StoreID === storeId) || null;
}

export const ShoppingCartSharedMethods = {
  // GetTotalShippingCostPerSKUAndStoreIDAsDouble

  getTotalShippingCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.skuOrderShippingCost) : 0;
  },

  // GetTotalDiscountCostPerSKUAndStoreIDAsDouble
  getTotalDiscountCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.SKUOrderDiscountTotal) : 0;
  },

  // GetTotalSubTotalCostPerSKUAndStoreIDAsDouble
  getTotalSubTotalCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.SKUOrderSubTotal) : 0;
  },

  // GetTotalTaxCostPerSKUAndStoreIDAsDouble
  getTotalTaxCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.BuyerTotalTaxCost) : 0;
  },

  // GetTotalCountryTaxCostPerSKUAndStoreIDAsDouble
  getTotalCountryTaxCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.BuyerCountryTotalTaxCost) : 0;
  },

  // GetTotalStateTaxCostPerSKUAndStoreIDAsDouble
  getTotalStateTaxCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.BuyerStateTotalTaxCost) : 0;
  },

  // GetTotalCityTaxCostPerSKUAndStoreIDAsDouble
  getTotalCityTaxCostPerSKUAndStoreIDAsDouble(
    skuId,
    storeId,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    const rec = findTotalsRecord(skuId, storeId, listOfAllShoppingCartTaxAndPaymentTotals);
    return rec ? convertStringToDouble(rec.BuyerCityTotalTaxCost) : 0;
  },

  // GetSelectedShippingMethodIDPerSKU
  getSelectedShippingMethodIDPerSKU(skuId, listOfAllShoppingCartSelectedShippingMethods) {
    if (
      !Array.isArray(listOfAllShoppingCartSelectedShippingMethods) ||
      !listOfAllShoppingCartSelectedShippingMethods.length
    )
      return 0;
    const rec = listOfAllShoppingCartSelectedShippingMethods.find(tt => tt.SKUID === skuId);
    return rec ? rec.SelectedShippingMethodID : 0;
  },

  // SKUFinalSeller
  skuFinalSeller(
    languageCode,
    skuId,
    listOfAllShoppingCartSelectedShippingMethods,
    listOfAllShoppingCartTaxAndPaymentTotals,
    listOfAllShoppingCartShippingMethodsAndPrices,
    listOfAllCanBuyNowOnlyShoppingCartItems
  ) {
    let result = 'Seller: Unknown';
    if (
      !listOfAllShoppingCartShippingMethodsAndPrices ||
      !listOfAllShoppingCartSelectedShippingMethods ||
      !listOfAllShoppingCartTaxAndPaymentTotals ||
      !listOfAllCanBuyNowOnlyShoppingCartItems
    )
      return result;

    const shipRec = listOfAllShoppingCartSelectedShippingMethods.find(tt => tt.SKUID === skuId);
    if (!shipRec) return result;

    const taxRec = listOfAllShoppingCartTaxAndPaymentTotals.find(
      tt => tt.SKUID === skuId && tt.IsValidTaxOptionCart
    );
    if (taxRec) {
      result = `Seller: ${taxRec.FinalSellingCompanyName}, ${taxRec.SellFromCountryName}`;
    } else {
      const itemRec = listOfAllCanBuyNowOnlyShoppingCartItems.find(tt => tt.SKUID === skuId);
      if (itemRec && itemRec.StoreName) {
        result = `Seller: ${itemRec.StoreName}`;
      }
    }
    return result;
  },

  // ConvertShippingMethodIDToShippingMethodNameTranslated
  convertShippingMethodIDToShippingMethodNameTranslated(languageCode, shippingMethodId) {
    switch (shippingMethodId) {
      case 1:
        return 'Express Shipping';
      case 2:
        return 'Shipped by Land';
      case 3:
        return 'Shipped by Sea';
      case 4:
        return 'Shipped by Air';
      default:
        return 'Unknown Shipping Method';
    }
  },

  // GetEstimatedDeliveryDatePerSKUTranslated
  getEstimatedDeliveryDatePerSKUTranslated(
    languageCode,
    skuId,
    listOfAllShoppingCartShippingMethodsAndPrices,
    listOfAllShoppingCartSelectedShippingMethods
  ) {
    let result = 'Estimated Delivery - Unknown';
    if (
      !listOfAllShoppingCartShippingMethodsAndPrices ||
      !listOfAllShoppingCartSelectedShippingMethods
    )
      return result;

    const shipRec = listOfAllShoppingCartSelectedShippingMethods.find(tt => tt.skuid === skuId);
    if (!shipRec) return result + 'l1';

    const optRec = listOfAllShoppingCartShippingMethodsAndPrices.find(
      tt => tt.skuid === skuId && tt.shippingMethodID === shipRec.selectedShippingMethodID
    );
    if (!optRec) return result + 'l2';

    const tempEstDeliveryDays = optRec.estDeliveryDays;
    if (!tempEstDeliveryDays) {
      return result + 'l3';
    }

    const estDaysX = convertStringToDouble(tempEstDeliveryDays);
    const today = new Date();
    today.setUTCDate(today.getUTCDate() + estDaysX);

    const locale = languageCode === 'ar' ? 'ar-LB' : languageCode;
    const options = { weekday: 'long', month: 'long', day: '2-digit' };
    const formatted = new Intl.DateTimeFormat(locale, options).format(today);

    return `Estimated Delivery - ${formatted}`;
  },

  //public static bool SKUHasValidTaxOptionForCart(int tempSKUID, List<ShoppingCartShippingMethodModel> listOfAllShoppingCartShippingMethodsAndPrices, List<ShoppingCartSelectedShippingMethodModel> listOfAllShoppingCartSelectedShippingMethods, List<ShoppingCartTaxAndPaymentTotalsModel> listOfAllShoppingCartTaxAndPaymentTotals)
  //{
  //    bool result = false;

  //    if (listOfAllShoppingCartShippingMethodsAndPrices != null && listOfAllShoppingCartSelectedShippingMethods != null)
  //    {

  //        // find the selected tax row or payment row for sku
  //        ShoppingCartSelectedShippingMethodModel rwCkeckIfAlreadyInTableShipping = listOfAllShoppingCartSelectedShippingMethods.FirstOrDefault(tt => tt.SKUID == tempSKUID);

  //        if (rwCkeckIfAlreadyInTableShipping != null)
  //        {

  //            int shippingMethodID = rwCkeckIfAlreadyInTableShipping.SelectedShippingMethodID;

  //            ShoppingCartTaxAndPaymentTotalsModel rwCkeckIfAlreadyInTableTaxCost = listOfAllShoppingCartTaxAndPaymentTotals.FirstOrDefault(tt => tt.SKUID == tempSKUID && tt.IsValidTaxOptionCart == true);

  //            if (rwCkeckIfAlreadyInTableTaxCost != null)
  //            {

  //                result = true;

  //            }

  //        }

  //    }

  //    return result;

  //}

  // SKUHasValidTaxOptionForCart
  skuHasValidTaxOptionForCart(
    skuId,
    listOfAllShoppingCartShippingMethodsAndPrices,
    listOfAllShoppingCartSelectedShippingMethods,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    if (
      !listOfAllShoppingCartShippingMethodsAndPrices ||
      !listOfAllShoppingCartSelectedShippingMethods ||
      !listOfAllShoppingCartTaxAndPaymentTotals
    )
      return false;

    const shipRec = listOfAllShoppingCartSelectedShippingMethods.find(tt => tt.skuid === skuId);

    if (!shipRec) return false;
    return listOfAllShoppingCartTaxAndPaymentTotals.some(
      tt => tt.skuid === skuId && tt.isValidTaxOptionCart
    );
  },

  //public static bool SKUHasValidShippingOptionForCart(int tempSKUID, List<ShoppingCartShippingMethodModel> listOfAllShoppingCartShippingMethodsAndPrices, List<ShoppingCartSelectedShippingMethodModel> listOfAllShoppingCartSelectedShippingMethods)
  //    {
  //        bool result = false;
  //    if (listOfAllShoppingCartShippingMethodsAndPrices != null && listOfAllShoppingCartSelectedShippingMethods != null)
  //    {
  //        // find the selected shipping method for sku
  //        ShoppingCartSelectedShippingMethodModel rwCkeckIfAlreadyInTableShipping = listOfAllShoppingCartSelectedShippingMethods.FirstOrDefault(tt => tt.SKUID == tempSKUID);
  //    if (rwCkeckIfAlreadyInTableShipping != null)
  //    {
  //        int shippingMethodID = rwCkeckIfAlreadyInTableShipping.SelectedShippingMethodID;
  //                ShoppingCartShippingMethodModel rwCkeckIfAlreadyInTableShippingOptions = listOfAllShoppingCartShippingMethodsAndPrices.FirstOrDefault(tt => tt.SKUID == tempSKUID && tt.ShippingMethodID == shippingMethodID);
  //    if (rwCkeckIfAlreadyInTableShippingOptions != null)
  //    {
  //        result = true;
  //                }
  //            }
  //        }
  //    return result;
  //    }

  // SKUHasValidShippingOptionForCart
  skuHasValidShippingOptionForCart(
    skuId,
    listOfAllShoppingCartShippingMethodsAndPrices,
    listOfAllShoppingCartSelectedShippingMethods
  ) {
    return true;
    if (
      !listOfAllShoppingCartShippingMethodsAndPrices ||
      !listOfAllShoppingCartSelectedShippingMethods
    )
      return false;

    const shipRec = listOfAllShoppingCartSelectedShippingMethods.find(tt => tt.skuid === skuId);

    if (!shipRec) return false;

    return listOfAllShoppingCartShippingMethodsAndPrices.some(
      tt =>
        tt.skuid === skuId &&
        tt.shippingMethodID === shipRec.selectedShippingMethodID &&
        tt.isValidShippingOptionCart
    );
  },

  // GetShippingOptionsAvailableForDropDown
  getShippingOptionsAvailableForDropDown(skuId, listOfAllShoppingCartShippingMethodsAndPrices) {
    if (!listOfAllShoppingCartShippingMethodsAndPrices || skuId == null) return [];
    return listOfAllShoppingCartShippingMethodsAndPrices.filter(tt => tt.skuid === skuId);
  },

  // GetSKUCurrentSelectedShippingMethodFromViewState
  getSKUCurrentSelectedShippingMethodFromViewState(
    skuId,
    listOfAllShoppingCartSelectedShippingMethods
  ) {
    return this.getSelectedShippingMethodIDPerSKU(
      skuId,
      listOfAllShoppingCartSelectedShippingMethods
    );
  },

  computeCartTotals(
    listOfAllCanBuyNowOnlyShoppingCartItems,
    listOfAllShoppingCartShippingMethodsAndPrices,
    listOfAllShoppingCartSelectedShippingMethods,
    listOfAllShoppingCartTaxAndPaymentTotals
  ) {
    let cartSubTotalAmountX = 0;
    let cartTotalShippingX = 0;
    let cartTotalDiscountX = 0;
    let cartTotalTaxX = 0;
    let cartTotalItemsCountX = 0;

    for (const row of listOfAllCanBuyNowOnlyShoppingCartItems || []) {
      // 1) validate shipping option
      const selectedShip = (listOfAllShoppingCartSelectedShippingMethods || []).find(
        o => o.skuid === row.skuid
      );

      const shipValid = selectedShip
        ? Boolean(
            (listOfAllShoppingCartShippingMethodsAndPrices || []).find(
              o =>
                o.skuid === row.skuid &&
                o.shippingMethodID === selectedShip.selectedShippingMethodID &&
                o.isValidShippingOptionCart
            )
          )
        : false;

      if (!shipValid) continue;

      // 2) find tax+payment totals row
      const taxRow = (listOfAllShoppingCartTaxAndPaymentTotals || []).find(
        o => o.skuid === row.skuid && o.isValidTaxOptionCart
      );

      if (!taxRow) continue;

      cartSubTotalAmountX += convertStringToDouble(taxRow.skuOrderSubTotal);
      cartTotalShippingX += convertStringToDouble(taxRow.skuOrderShippingCost);
      cartTotalDiscountX += convertStringToDouble(taxRow.skuOrderDiscountTotal);
      cartTotalTaxX += convertStringToDouble(taxRow.buyerTotalTaxCost);
      cartTotalItemsCountX += Number(taxRow.quantity);
    }

    const cartSubTotalPlusDiscountAmountX = cartSubTotalAmountX + cartTotalDiscountX;
    const cartTotalAmountX =
      cartSubTotalPlusDiscountAmountX + cartTotalShippingX + cartTotalTaxX - cartTotalDiscountX;

    return {
      cartSubTotalAmountX,
      cartSubTotalPlusDiscountAmountX,
      cartTotalShippingX,
      cartTotalDiscountX,
      cartTotalTaxX,
      cartTotalAmountX,
      cartTotalItemsCountX,
    };
  },
};
